import React from "react";
import { Box, Card, Flex, Stack, Text } from "@chakra-ui/react";
import { LuMail, LuWallet } from "react-icons/lu";
import { Button } from "../../components/ui/button";
import { useGlobalState } from "../../helpers/GlobalStateProvider";
import { useModalState } from "../../helpers/ModalProvider";

function shortAddress(addr: string): string {
  if (addr.length <= 12) return addr;
  return `${addr.slice(0, 6)}…${addr.slice(-4)}`;
}

const AccountAuthSection: React.FC = () => {
  const { info } = useGlobalState();
  const {
    showAddEmailModal,
    showUpdateAuthModal,
    showUpdateAuthPasswordModal,
    showDeleteAuthModal,
  } = useModalState();

  const hasEmail = !!info?.email;
  const hasWallet = !!info?.walletAddress;
  const onlyOneAuthMethod = (hasEmail && !hasWallet) || (hasWallet && !hasEmail);

  return (
    <Card.Root>
      <Card.Header>
        <Card.Title fontSize="30px" fontWeight="bold" letterSpacing="-0.4px" lineHeight="32px">
          Account & Authentication
        </Card.Title>
        <Card.Description fontSize="md" color="#52525b" lineHeight="24px">
          Manage how you sign in to your CoinCap account.
        </Card.Description>
      </Card.Header>
      <Card.Body>
        {/* Email subsection */}
        <Flex alignItems="flex-start" gap={3} mb={5}>
          <Box color="#7c3aed" pt="2px">
            <LuMail size={20} />
          </Box>
          <Stack flex={1} gap={2}>
            <Text fontSize="md" fontWeight="bold" color="#000">
              Email
            </Text>
            {hasEmail ? (
              <>
                <Text fontSize="sm" fontWeight="semibold" color="#000">
                  {info.email}
                </Text>
                <Flex gap={2} flexWrap="wrap">
                  <Button size="sm" variant="outline" onClick={() => showUpdateAuthModal()}>
                    Change email
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => showUpdateAuthPasswordModal()}>
                    Change password
                  </Button>
                  {!onlyOneAuthMethod && (
                    <Button
                      size="sm"
                      variant="outline"
                      colorPalette="red"
                      onClick={() => showDeleteAuthModal("email", info.email || "")}
                    >
                      Remove
                    </Button>
                  )}
                </Flex>
              </>
            ) : (
              <Flex gap={2}>
                <Button size="sm" colorPalette="purple" variant="outline" onClick={() => showAddEmailModal()}>
                  + Add email
                </Button>
              </Flex>
            )}
          </Stack>
        </Flex>

        {/* Divider */}
        <Box height="1px" bg="#e4e4e7" mb={5} />

        {/* Wallet subsection */}
        <Flex alignItems="flex-start" gap={3}>
          <Box color="#7c3aed" pt="2px">
            <LuWallet size={20} />
          </Box>
          <Stack flex={1} gap={2}>
            <Text fontSize="md" fontWeight="bold" color="#000">
              Wallet
            </Text>
            {hasWallet ? (
              <>
                <Text fontSize="sm" fontWeight="semibold" color="#000" fontFamily="monospace">
                  {shortAddress(info.walletAddress)}
                </Text>
                {!onlyOneAuthMethod && (
                  <Flex gap={2}>
                    <Button
                      size="sm"
                      variant="outline"
                      colorPalette="red"
                      onClick={() => showDeleteAuthModal("wallet", info.walletAddress || "")}
                    >
                      Disconnect
                    </Button>
                  </Flex>
                )}
              </>
            ) : (
              <>
                <Text fontSize="sm" color="#a1a1aa" fontStyle="italic">
                  No wallet connected
                </Text>
                <Flex gap={2}>
                  <ConnectWalletButton />
                </Flex>
              </>
            )}
          </Stack>
        </Flex>
      </Card.Body>
    </Card.Root>
  );
};

// Local subcomponent — wraps connectAndAssociateWallet with error fallback
const ConnectWalletButton: React.FC = () => {
  const { connectAndAssociateWallet } = useGlobalState();
  const { showCannotConnectWalletModal } = useModalState();
  return (
    <Button
      size="sm"
      colorPalette="purple"
      variant="outline"
      onClick={async () => {
        try {
          await connectAndAssociateWallet();
        } catch {
          showCannotConnectWalletModal();
        }
      }}
    >
      Connect wallet
    </Button>
  );
};

export default AccountAuthSection;
